import { useState, useCallback } from "react";
import { toast } from "react-hot-toast";
import i18next from "i18next";

import parseError from "../../utils/parseError";

/**
 * Run a single-row mutation across an array of ids and shape the result
 * like the backend bulk endpoints do (`{updated:[], failed:[...]}`).
 *
 * Use this for modules where the backend hasn't shipped a real bulk
 * endpoint — Coupons (toggle is_active) and Customers (status) — so the
 * BulkActionBar UX is identical no matter the backend shape.
 *
 *   const { run, isRunning, progress } = useBulkClientIterate();
 *   const result = await run(ids, (id) => updateCoupon(id, {is_active: true}));
 *
 * Result: `{ updated: int[], failed: [{id, reason: 'request_failed', message}] }`.
 */
const useBulkClientIterate = () => {
    const [isRunning, setIsRunning] = useState(false);
    const [progress, setProgress] = useState({ done: 0, total: 0 });

    const run = useCallback(async (ids, callerFn) => {
        if (!Array.isArray(ids) || ids.length === 0) {
            return { updated: [], failed: [] };
        }
        const isArabic = (i18next.language || "en").startsWith("ar");

        setIsRunning(true);
        setProgress({ done: 0, total: ids.length });

        const updated = [];
        const failed = [];

        // Sequential — gentle on the API, predictable failure ordering.
        for (let i = 0; i < ids.length; i += 1) {
            const id = ids[i];
            try {
                // eslint-disable-next-line no-await-in-loop
                await callerFn(id);
                updated.push(id);
            } catch (err) {
                failed.push({
                    id,
                    reason: "request_failed",
                    message: parseError(err) || (isArabic ? "فشل الطلب" : "Request failed"),
                });
            }
            setProgress({ done: i + 1, total: ids.length });
        }

        setIsRunning(false);

        if (failed.length === 0) {
            toast.success(
                isArabic
                    ? `تم تحديث ${updated.length} من ${ids.length}`
                    : `Updated ${updated.length} of ${ids.length}`
            );
        } else if (updated.length === 0) {
            toast.error(
                isArabic
                    ? `فشل تحديث جميع الأسطر (${failed.length})`
                    : `All ${failed.length} rows failed`
            );
        } else {
            toast(
                isArabic
                    ? `تم تحديث ${updated.length} من ${ids.length}، فشل ${failed.length}`
                    : `Updated ${updated.length} of ${ids.length}, ${failed.length} failed`,
                { icon: "⚠️" }
            );
        }

        return { updated, failed };
    }, []);

    return { run, isRunning, progress };
};

export default useBulkClientIterate;
